import React from 'react';
import brandConfig from '../../config/brandConfig';
import { Shield, Award, Users, Home, ArrowRight, Sparkles } from 'lucide-react';
import { COUNTRY_CONFIG } from '../../config/countryConfig';

const HeroSection = () => {
  const countries = Object.entries(COUNTRY_CONFIG).map(([key, config]) => ({
    key,
    code: config.code,
    name: config.name,
    flag: config.flag
  }));

  const handleCountryClick = (countryKey) => {
    const isDev = window.location.hostname === 'localhost';
    const countryCode = COUNTRY_CONFIG[countryKey]?.code || 'PE';

    if (isDev) {
      window.location.href = `http://localhost:3001?country=${countryCode}`;
    } else {
      window.location.href = `https://${countryKey}.lotespro.com`;
    }
  };

  return (
    <div className="relative overflow-hidden grain-overlay">
      {/* Dynamic gradient background */}
      <div className="absolute inset-0 animate-gradient" style={{
        background: 'linear-gradient(135deg, #0a1929 0%, #102a43 25%, #1a3a5c 50%, #243b53 75%, #0a1929 100%)'
      }} />

      {/* Geometric shapes */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.07]" style={{
          background: `radial-gradient(circle, ${brandConfig.primaryColor} 0%, transparent 70%)`
        }} />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full opacity-[0.05]" style={{
          background: `radial-gradient(circle, ${brandConfig.secondaryColor} 0%, transparent 70%)`
        }} />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full opacity-[0.03]" style={{
          background: 'radial-gradient(circle, #ffffff 0%, transparent 70%)'
        }} />
      </div>

      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-36">
        <div className="text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full mb-8 animate-fade-in-up glass" style={{ animationDelay: '0.1s' }}>
            <Sparkles className="w-4 h-4" style={{ color: brandConfig.secondaryColor }} />
            <span className="text-sm font-semibold text-white/90">Plataforma #1 en Latinoamerica</span>
          </div>

          {/* Logo */}
          <div className="mb-8 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl shadow-2xl bg-white p-0.5 animate-pulse-glow">
              <img
                src="/images/android-chrome-192x192.png"
                alt={brandConfig.getLogoAlt()}
                width={80}
                height={80}
                className="rounded-2xl"
              />
            </div>
          </div>

          {/* Headline */}
          <h1 className="animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <span className="block text-5xl lg:text-7xl font-extrabold tracking-tight mb-2">
              <span style={{ color: brandConfig.secondaryColor }}>{brandConfig.namePrefix.toLowerCase()}</span>
              <span className="text-white">{brandConfig.nameSuffix}</span>
            </span>
          </h1>

          <p className="text-xl lg:text-2xl text-white/60 font-light max-w-2xl mx-auto mb-6 animate-fade-in-up font-display italic" style={{ animationDelay: '0.4s' }}>
            La plataforma inmobiliaria lider en Latinoamerica
          </p>

          {/* Value proposition */}
          <p className="text-base lg:text-lg text-white/50 leading-relaxed max-w-3xl mx-auto mb-12 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
            Conectamos rapidamente <span className="font-bold text-white/80">miles de clientes</span> gracias a nuestra{' '}
            <span className="font-bold text-white/80">eficiencia comprobada</span> en generar ventas, cotizaciones y reservas.
            Una plataforma <span className="font-bold text-white/80">completa e integral</span> para la gestion profesional de lotes inmobiliarios.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
            <a
              href={`${brandConfig.dashboardUrl}/iniciar-sesion`}
              className="group flex items-center gap-3 px-8 py-4 rounded-2xl font-bold text-lg text-white transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-[1.03]"
              style={{ background: brandConfig.getPrimaryGradient() }}
            >
              Comenzar Gratis
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href={brandConfig.projectDemoUrl}
              className="group flex items-center gap-3 px-8 py-4 rounded-2xl font-bold text-lg text-white/90 hover:text-white transition-all duration-300 glass hover:bg-white/15"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Ver Demo en Vivo
            </a>
          </div>

          {/* Country selector heading */}
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/40 mb-6 animate-fade-in-up" style={{ animationDelay: '0.7s' }}>
            Selecciona tu pais
          </p>

          {/* Country Cards Grid */}
          <div className="max-w-6xl mx-auto mb-20 animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
            <div className="flex flex-wrap justify-center gap-3">
              {countries.map((country, i) => (
                <button
                  key={country.key}
                  onClick={() => handleCountryClick(country.key)}
                  className="group flex flex-col items-center justify-center p-3 sm:p-4 rounded-2xl transition-all duration-300 glass hover:bg-white/20 hover:scale-110 hover:shadow-xl hover:shadow-orange-500/10 w-[calc(33.33%-8px)] sm:w-[100px] lg:w-[110px]"
                  title={`Ver lotes en ${country.name}`}
                >
                  <span className="text-3xl sm:text-4xl mb-1.5 group-hover:scale-110 transition-transform duration-300">{country.flag}</span>
                  <span className="text-[10px] sm:text-xs font-bold text-center leading-tight text-white/70 group-hover:text-white transition-colors duration-300">
                    {country.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 max-w-5xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.9s' }}>
            {[
              { icon: <Home className="w-6 h-6" />, value: '2,500+', label: 'Propiedades', color: brandConfig.primaryColor },
              { icon: <Award className="w-6 h-6" />, value: '150+', label: 'Inmobiliarias', color: brandConfig.secondaryColor },
              { icon: <Users className="w-6 h-6" />, value: '15,000+', label: 'Clientes', color: '#38bdf8' },
              { icon: <Shield className="w-6 h-6" />, value: '19', label: 'Paises', color: '#34d399' }
            ].map((stat, i) => (
              <div key={i} className="text-center group glass rounded-2xl p-6 hover:bg-white/10 transition-all duration-300">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-3 group-hover:scale-110 transition-transform duration-300" style={{ background: `${stat.color}20` }}>
                  <div style={{ color: stat.color }}>{stat.icon}</div>
                </div>
                <div className="text-3xl lg:text-4xl font-extrabold text-white mb-1 tracking-tight">{stat.value}</div>
                <div className="text-sm text-white/50 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Trust bar */}
          <div className="mt-16 flex flex-wrap justify-center items-center gap-8 text-white/30 animate-fade-in-up" style={{ animationDelay: '1s' }}>
            {[
              { icon: <Shield className="w-4 h-4" />, text: 'Transacciones Seguras' },
              { icon: <Award className="w-4 h-4" />, text: 'Certificaciones' },
              { icon: <Users className="w-4 h-4" />, text: 'Soporte 24/7' }
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                {item.icon}
                <span className="text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 80V40C240 10 480 0 720 10C960 20 1200 50 1440 40V80H0Z" className="fill-white dark:fill-gray-900" />
        </svg>
      </div>
    </div>
  );
};

export default HeroSection;
